import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Home,
  Package,
  BookOpen,
  Info,
  Mail,
  UserPlus,
  LogIn,
  FileText,
  Store,
  ChevronLeft,
} from 'lucide-react';
import SaasContainer from '../saas/Container';

const DASHBOARD_PATHS = [
  '/admin',
  '/business',
  '/business-registration',
  '/business-pending',
  '/business-dashboard',
  '/merchant',
  '/merchant-pending',
  '/merchant/products',
  '/merchant/add-product',
  '/supplier',
  '/supplier/products',
  '/supplier/add-product',
  '/user',
  '/user/profile',
];

function FooterMobileTile({ to, icon: Icon, label }) {
  return (
    <Link to={to} className="footer-mobile-tile group">
      <span className="footer-mobile-tile__icon">
        <Icon className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
      </span>
      <span className="footer-mobile-tile__label">{label}</span>
    </Link>
  );
}

function FooterMobileRowLink({ to, label, accent = false }) {
  return (
    <Link
      to={to}
      className={`footer-mobile-row group ${accent ? 'footer-mobile-row--accent' : ''}`}
    >
      <span>{label}</span>
      <ChevronLeft className="h-4 w-4 shrink-0 opacity-40 transition-transform group-hover:-translate-x-0.5" aria-hidden />
    </Link>
  );
}

const Footer = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isDashboardPage = DASHBOARD_PATHS.includes(location.pathname);

  const linkMuted = 'type-nav-link text-slate-600 transition-colors hover:text-signal';
  const linkAccent = 'type-nav-link font-semibold text-signal transition-opacity hover:opacity-85';
  const colTitle = 'type-eyebrow mb-4 md:mb-5 lg:text-[10px] lg:tracking-[0.18em] text-steel';

  const exploreLinks = [
    { to: '/', label: t('footer.home'), icon: Home },
    { to: '/products', label: t('footer.ourServices'), icon: Package },
    { to: '/blog', label: t('footer.blog'), icon: BookOpen },
    { to: '/about', label: t('footer.about'), icon: Info },
    { to: '/contact', label: t('footer.contact'), icon: Mail },
  ];

  const accountLinks = [
    { to: '/register', label: t('footer.register'), icon: UserPlus },
    { to: '/login', label: t('nav.login'), icon: LogIn },
    { to: '/terms', label: t('footer.terms'), icon: FileText },
  ];

  const partnerLinks = [
    { to: '/join', label: t('footer.linkPartnersHome'), accent: true },
    { to: '/login/business', label: t('footer.linkBusinessLogin') },
    { to: '/login/trader', label: t('footer.linkTraderLogin') },
    { to: '/register/business', label: t('footer.linkBusinessRegister') },
    { to: '/register/trader', label: t('footer.linkTraderRegister') },
  ];

  if (isDashboardPage) {
    return null;
  }

  return (
    <footer
      className={`relative mt-auto overflow-hidden border-t border-hairline/40 bg-linen-canvas ${
        isHomePage ? 'pb-[calc(5.5rem+env(safe-area-inset-bottom))] md:pb-0' : ''
      }`}
    >
      {/* نوار فروشندگان */}
      <div className="relative border-b border-hairline/40 bg-gradient-to-tr from-[#714b67]/[0.07] via-sky-wash/40 to-linen-canvas">
        <div className="pointer-events-none absolute inset-y-0 end-0 w-1/2 max-w-md bg-[radial-gradient(ellipse_80%_80%_at_80%_50%,rgba(113,75,103,0.12),transparent_70%)]" aria-hidden />
        <SaasContainer className="relative flex flex-col gap-4 py-6 md:flex-row md:items-center md:justify-between md:gap-8 md:py-10">
          <div className="max-w-xl space-y-1.5 md:space-y-2">
            <p className="type-eyebrow text-signal/90">{t('footer.ribbonEyebrow')}</p>
            <p className="type-card-title text-balance text-base md:text-xl">{t('footer.ribbonHeading')}</p>
            <p className="text-[13px] leading-relaxed text-steel md:text-sm">{t('footer.ribbonSub')}</p>
          </div>
          <Link
            to="/join"
            className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-full bg-ink-graphite px-6 py-3 text-sm font-semibold text-white shadow-[0_14px_36px_-12px_rgba(20,20,30,0.55)] transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-[0_18px_44px_-14px_rgba(113,75,103,0.35)] md:w-auto md:self-center"
          >
            {t('footer.ribbonCta')}
            <svg className="h-4 w-4 opacity-90" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </Link>
        </SaasContainer>
      </div>

      <SaasContainer className="py-8 md:py-16 lg:py-[4.5rem]">
        {/* ── موبایل ── */}
        <div className="space-y-5 md:hidden">
          <div className="flex items-center gap-3">
            <img src="/jet-icon.png" alt="" width={44} height={44} className="h-11 w-11 rounded-[22%] object-cover ring-2 ring-white shadow-sm" />
            <div className="min-w-0">
              <p className="type-card-title text-base">{t('hero.title', { defaultValue: 'فلاشر' })}</p>
              <p className="text-[12px] leading-snug text-steel">{t('footer.tagline')}</p>
            </div>
          </div>

          <div>
            <p className="type-eyebrow mb-2.5 text-steel">{t('footer.sectionExplore')}</p>
            <div className="grid grid-cols-2 gap-2">
              {exploreLinks.map((item) => (
                <FooterMobileTile key={item.to} {...item} />
              ))}
            </div>
          </div>

          <div className="footer-mobile-panel">
            <p className="type-eyebrow mb-2 text-steel">{t('footer.sectionAccount')}</p>
            <div className="grid grid-cols-3 gap-2">
              {accountLinks.map((item) => (
                <Link key={item.to} to={item.to} className="footer-mobile-pill">
                  <item.icon className="h-4 w-4 shrink-0" strokeWidth={2} aria-hidden />
                  <span className="truncate">{item.label}</span>
                </Link>
              ))}
            </div>
          </div>

          <div className="footer-mobile-panel footer-mobile-panel--partners">
            <div className="mb-2 flex items-center gap-2">
              <Store className="h-4 w-4 text-signal" strokeWidth={2} aria-hidden />
              <p className="type-eyebrow text-signal/90">{t('footer.sectionPartners')}</p>
            </div>
            <div className="divide-y divide-hairline/35 overflow-hidden rounded-xl border border-hairline/40 bg-card-surface">
              {partnerLinks.map((item) => (
                <FooterMobileRowLink key={item.to} {...item} />
              ))}
            </div>
          </div>

        </div>

        {/* ── دسکتاپ ── */}
        <div className="hidden md:grid md:grid-cols-3 md:gap-x-10 md:gap-y-14">
          <div>
            <h3 className={colTitle}>{t('footer.sectionExplore')}</h3>
            <ul className="flex flex-col gap-2.5">
              {exploreLinks.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className={linkMuted}>
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className={colTitle}>{t('footer.sectionAccount')}</h3>
            <ul className="flex flex-col gap-2.5">
              {accountLinks.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className={linkMuted}>
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className={colTitle}>{t('footer.sectionPartners')}</h3>
            <ul className="flex flex-col gap-2.5">
              {partnerLinks.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className={item.accent ? linkAccent : linkMuted}>
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </SaasContainer>

      <div className="border-t border-hairline/40 bg-black/[0.02] py-5 md:py-8">
        <SaasContainer className="flex flex-col items-center gap-2 text-center md:flex-row md:items-center md:justify-between md:text-start">
          <p className="text-sm font-semibold tracking-tight text-ink-graphite md:max-w-md md:text-lg">{t('footer.tagline')}</p>
          <p className="text-[11px] text-steel md:text-[12px]">{t('footer.rights')} © ۱۴۰۵</p>
        </SaasContainer>
      </div>
    </footer>
  );
};

export default Footer;
